import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IconChevronRight, IconBolt } from '@tabler/icons-react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';
import { MToggle } from '@/components/ui/atoms/MToggle';
import { useNotificationSettings } from '@/hooks/useNotificationSettings';
import { useToast } from '@/components/ui/atoms/MToastProvider';
import { NavigationButton } from '@/components/ui/atoms/MNavigationButton';
import { notificationOptions as baseNotificationOptions } from '@/types/notificationSettings';
import { notificationKeyMap } from '@/types/notificationSettings';
import { getMemberInfo, useMembers } from "@/hooks/useMembers";
import { useProfileUpdate } from '@/hooks/useMembers';
import { useFamily } from '@/hooks/useFamily';
import FormHeader from '@/components/ui/organisms/MHeader';
import MSpacer from '@/components/ui/atoms/MSpacer';
import { useTranslation } from 'react-i18next';
import { inputFieldDate } from '@/utils/date-utils';
import { memberNormalizer } from '@/utils/memberNormalizer';
import styles from './settings.module.css';

const notificationOptions = baseNotificationOptions;

const Notifications: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const memberInfo = getMemberInfo();
  const { updateMember } = useMembers();
  const { updateMemberInLocalStorage } = useProfileUpdate();
  const { member, selectedMember } = useSelector((state: RootState) => ({
    member: state.auth?.member,
    selectedMember: state.auth?.selectedMember,
  }));
  const { data: familyMembers = [] } = useFamily();
  const displayMember = selectedMember || member;
  const { settings, saving, updateSettings } = useNotificationSettings();

  // Insights state from member other_attrs
  const [insightsEnabled, setInsightsEnabled] = React.useState(
    memberInfo?.other_attrs?.insights || false
  );
  const [updatingInsights, setUpdatingInsights] = React.useState(false);

  // Handle Insights toggle
  const handleInsightsToggle = async () => {
    if (!displayMember?.id) return;
    
    setUpdatingInsights(true);
    try {
      const newValue = !insightsEnabled;
      const updatedOtherAttrs = {
        ...(memberInfo.other_attrs || {}),
        insights: newValue
      };
      
      // Update backend first
      await updateMember(displayMember.id, {
        other_attrs: updatedOtherAttrs
      });
      
      // Then update local state and Redux (this internally calls doUpdateMember)
      await updateMemberInLocalStorage(displayMember.id, memberInfo.onBehalf);
      
      setInsightsEnabled(newValue);
    } catch (err) {
      toast.showToast({ message: 'Please try again.', variant: 'error' });
    } finally {
      setUpdatingInsights(false);
    }
  };

  const handleToggle = async (key: string) => {
    if (!settings) return;
    const backendKey = notificationKeyMap[key as keyof typeof notificationKeyMap] as keyof typeof settings;
    try {
      await updateSettings({ [backendKey]: !settings[backendKey] });
      //toast.showToast({ message: 'Notification settings updated', variant: 'success' });
    } catch (err) {
      toast.showToast({ message: 'Please try again.', variant: 'error' });
    }
  };

  const handleDeleteAccount = async () => {
    const isSelf = true;
    const isFO = displayMember?.family_member_type === "fo";
    let eligibleForOwnership: any[] = [];
    if (isSelf && isFO && Array.isArray(familyMembers)) {
      eligibleForOwnership = familyMembers.filter(
        (m: any) =>
          m.registered_member &&
          (m.id || m._id) !== (displayMember.id || displayMember._id) &&
          m.family_member_type !== "fo"
      );
    }
    const normalizedMember = memberNormalizer(displayMember);
    navigate("/profile/family/delete-member", {
      state: {
        action: "fo-delete-registered",
        targetMember: normalizedMember,
        eligibleForOwnership,
      },
    });
  };

  return (
    <div className="profile-page-root">
      <FormHeader
        module="notifications"
        memberInfo={memberInfo}
        title="App settings"
        description="Set up how you'd like to use Maatra so it fits your comfort and routine."
      />
      <MSpacer mt={24} />
      
      {/* Notification Settings */}
      {settings && notificationOptions.map((option) => (
        <div key={option.key} className={styles.notificationSettingsContainer}>
          <div className={styles.notificationSettingsTextContainer}>
            <p className='body-base-semibold' style={{ color: 'var(--brand-colours-indigo-indigo-700)' }}>{t(`notifications.options.${option.key}`)}</p>
            <p className='body-xs-medium' style={{ color: 'var(--colour-text-default)' }}>{t(`notifications.options.${option.key}.description`)}</p>
          </div>
          <MToggle
            checked={settings[notificationKeyMap[option.key as keyof typeof notificationKeyMap] as keyof typeof settings]}
            onChange={() => handleToggle(option.key)}
            disabled={saving}
          />
        </div>
      ))}
      
      {/* Insights Toggle */}
      <div className={styles.notificationSettingsContainer}>
        <div className={styles.notificationSettingsTextContainer}>
          <div className="flex items-center gap-2">
            <p className='body-base-semibold' style={{ color: 'var(--brand-colours-indigo-indigo-700)' }}>AI insights</p>
          </div>
          <p className='body-xs-medium' style={{ color: 'var(--colour-text-default)' }}>Turn off to disable document insights and your health summary</p>
        </div>
        <MToggle
          checked={insightsEnabled}
          onChange={handleInsightsToggle}
          disabled={updatingInsights}
        />
      </div>
      
      {/* Consent Withdrawal */}
      <NavigationButton
        text={t('privacy.consentWithdrawal')}
        rightIcon={<IconChevronRight />}
        onClick={() => navigate('/profile/notifications/consent-withdrawal')}
        className={styles.settingsButton}
      />

      {/* OSS Licenses */}
      <NavigationButton
        text="OSS licenses"
        rightIcon={<IconChevronRight />}
        onClick={() => navigate('/profile/notifications/oss-licenses')}
        className={styles.settingsButton}
      />

      {/* Last Login */}
      <NavigationButton
        text={t('privacy.lastLogin')}
        info={memberInfo.lastLogin ? inputFieldDate(memberInfo.lastLogin) : '—'}
        className={styles.settingsButton}
      />

      {/* Logged in as */}
      <NavigationButton
        text="Logged in as"
        info={(() => {
          const preferredLoginMethod = localStorage.getItem('preferred_login_method');
          if (preferredLoginMethod === 'email') {
            return memberInfo.email || '—';
          } else {
            return memberInfo.mobile ? `+91 ${memberInfo.mobile}` : '—';
          }
        })()}
        className={styles.settingsButton}
      />

      {/* Delete Account (only for logged-in member) */}
      {displayMember?.id === member?.id && (
        <div className={styles.deleteAccountContainer}>
          <NavigationButton
            key="delete"
            variant="delete"
            leftIcon="Trash"
            text={t("basicInfo.deleteAccount")}
            onClick={handleDeleteAccount}
          />
        </div>
      )}
    </div>
  );
};

export default Notifications;
